---
id: apps-app-react-auth-site-pages-doc
title: App React Auth Site Pages
owner: Luc LEMEREZ
status: Active
lastReviewed: 2026-02-19
visibility: internal
tags:
  - app
  - auth
  - frontend
  - pages
  - astro
---

## Product Overview

### Who it serves

This pages module serves unauthenticated and recovering users entering Rezi
through account access routes (login, signup, email confirmation, and password
reset).

### Primary workflow

The route set starts from `/` (redirects to `/login`) and branches into:
`/login`, `/signup`, `/confirm-email`, `/reset`, `/reset-password`, campaign
signup pages (`/invite`, `/r`), and the resume-preserving onboarding route
`/signup-resume`.

Pages provide the user-facing form and OTP surfaces, while shared auth logic is
executed in the layout runtime. Successful flows route users into the main app,
typically `/dashboard/resumes` (or a preserved redirect target).

### Business rules

`/forgot-password` is normalized to `/reset` via redirect, and root traffic is
normalized to `/login`. OTP pages (`/confirm-email`, `/reset-password`) require
email context and six-digit verification before completion of email confirmation
or password update.

`/signup-resume` is a specialized signup path that preserves anonymous resume
context and presents a resume preview variant during account creation.

At the edge, these auth routes are served through the Cloudflare Worker
`astro-auth`, which proxies auth-page requests to the Netlify auth site and
falls back to the main origin for non-auth traffic.

### Outcomes

Users can authenticate, create accounts, confirm email ownership, or recover
password access through predictable route entry points with consistent behavior
across campaigns and direct links.

### Out of scope

These page files do not implement backend auth checks, OTP issuance policy, or
post-login dashboard feature behavior.

## Technical Overview

### Architecture flow

Each page is an Astro route under `apps/app-react-auth-site/src/pages` and is
pre-rendered (`export const prerender = true`). Most pages mount
`AuthLayout.astro` and expose structured HTML with `data-*` hooks that the
layout script binds to runtime behavior.

### Entry points

Route files map one-to-one to URLs:
`index.astro` (`/`), `login.astro` (`/login`), `signup.astro` (`/signup`),
`confirm-email.astro` (`/confirm-email`), `reset.astro` (`/reset`),
`reset-password.astro` (`/reset-password`), `forgot-password.astro`
(`redirect -> /reset`), `invite.astro` (`/invite`), `r.astro` (`/r`), and
`signup-resume.astro` (`/signup-resume`).

### Key modules and flows

Form pages declare action markers (`data-form-action="login"`,
`data-form-action="signup"`, `data-form-action="reset"`) and standard
`data-test-id` selectors used by the layout script for auth submission,
analytics, and transitions.

OTP pages expose `data-otp-root`, `data-otp-type`, `data-otp-digit`,
`data-otp-resend`, and state containers (`data-otp-loading`,
`data-otp-success`, `data-otp-error`, `data-password-section`) so the layout
can orchestrate send/verify/cooldown/reset-password states.

`signup-resume.astro` sets `rightPanelVariant="resume-skeleton"`, enabling
anonymous resume preview hydration after token verification handled by layout
logic.

### Integrations and side effects

Page files themselves are mostly declarative, but their markup contracts drive
runtime side effects in layout: Firebase auth actions, API Gateway calls,
reCAPTCHA execution, redirects, and event tracking. Social entry buttons are
exposed on login/signup pages through shared button/input components.

### Constraints and risks

Because page behavior depends on exact `data-*` contracts, changing attribute
names, route paths, or form structure without corresponding layout updates can
break auth and OTP flows. Route-level redirects (`/`, `/forgot-password`) must
remain aligned with worker routing and main-app expectations.

## Changelog

- 2026-02-19: Initial pages documentation for `apps/app-react-auth-site/src/pages`.
